/* ==========================================================================
   B4 Bourtange — theme.js
   Vanilla ES6+. No frameworks, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme || {};
  const routes = T.routes || {};
  const strings = T.strings || {};

  /* ---------------------------------------------------------------------
     Utils
     --------------------------------------------------------------------- */
  const qs = (sel, scope) => (scope || document).querySelector(sel);
  const qsa = (sel, scope) => Array.from((scope || document).querySelectorAll(sel));

  const on = (el, evt, handler, opts) => el && el.addEventListener(evt, handler, opts);

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  const Toast = (function () {
    let el, textEl, timer;

    function init() {
      el = qs('[data-toast]');
      textEl = qs('[data-toast-text]');
    }

    function show(message, isError) {
      if (!el || !textEl) return;
      clearTimeout(timer);
      textEl.textContent = message;
      el.classList.toggle('toast--error', !!isError);
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-visible'));
      timer = setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => {
          el.hidden = true;
        }, 300);
      }, 3200);
    }

    return { init, show };
  })();

  /* ---------------------------------------------------------------------
     Scroll reveal — IntersectionObserver with 90ms stagger per sibling
     --------------------------------------------------------------------- */
  const ScrollReveal = (function () {
    let observer;

    function init() {
      if (!document.body.classList.contains('reveal-enabled')) return;

      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        qsa('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;

            // Only auto-stagger when the author has not set an explicit delay.
            if (!el.style.getPropertyValue('--reveal-delay')) {
              const siblings = Array.from(el.parentElement ? el.parentElement.children : []).filter(
                (n) => n.hasAttribute && n.hasAttribute('data-reveal')
              );
              const idx = siblings.indexOf(el);
              if (idx > 0) el.style.setProperty('--reveal-delay', idx * 90 + 'ms');
            }

            el.classList.add('is-revealed');
            observer.unobserve(el);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );

      observe();
    }

    function observe(scope) {
      if (!observer) return;
      qsa('[data-reveal]:not(.is-revealed)', scope).forEach((el) => {
        // Already on screen (e.g. above-the-fold content at initial load) —
        // show it immediately instead of hiding it behind reveal-pending
        // and waiting on an IntersectionObserver callback.
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('is-revealed');
          return;
        }
        el.classList.add('reveal-pending');
        observer.observe(el);
      });
    }

    return { init, observe };
  })();

  /* ---------------------------------------------------------------------
     Images — IntersectionObserver fade-in + optional data-src upgrade
     --------------------------------------------------------------------- */
  const Images = (function () {
    let observer;

    function markLoaded(img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
      }
    }

    function init() {
      if (!('IntersectionObserver' in window)) {
        qsa('img.lazy-img').forEach((img) => img.classList.add('is-loaded'));
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const img = entry.target;

            if (img.dataset.src) {
              img.src = img.dataset.src;
              delete img.dataset.src;
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              delete img.dataset.srcset;
            }

            markLoaded(img);
            observer.unobserve(img);
          });
        },
        { rootMargin: '200px 0px' }
      );

      observe();
    }

    function observe(scope) {
      if (!observer) return;
      qsa('img.lazy-img:not(.is-loaded), img[data-src]', scope).forEach((img) => observer.observe(img));
    }

    return { init, observe };
  })();

  /* ---------------------------------------------------------------------
     Sticky header
     --------------------------------------------------------------------- */
  const Header = (function () {
    let header, announcement, lastY = 0;

    function init() {
      header = qs('[data-header]');
      if (!header) return;
      announcement = qs('.announcement');

      // Header is `position: fixed` (see theme.css for why `sticky` doesn't
      // engage in this markup structure), so it has no natural way to sit
      // below the announcement bar and then take over at the top like
      // `sticky` would — slide it up under the bar manually as that bar
      // scrolls out of view.
      const onScroll = () => {
        const y = window.scrollY || window.pageYOffset;
        header.classList.toggle('is-stuck', y > 40);
        if (announcement) {
          header.style.top = `${Math.max(0, announcement.offsetHeight - y)}px`;
        }
        lastY = y;
      };

      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }

    function height() {
      return header ? header.offsetHeight : 0;
    }

    return { init, height };
  })();

  /* ---------------------------------------------------------------------
     Drawers (mobile nav + cart) with focus trap and scroll lock
     --------------------------------------------------------------------- */
  const Drawers = (function () {
    // Tracked by name, not by element: cart sections get re-rendered while open.
    let overlay, activeName = null, lastFocused = null, scrollY = 0;

    function current() {
      return activeName ? qs(`[data-drawer="${activeName}"]`) : null;
    }

    function lockScroll() {
      scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('is-locked');
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }

    function unlockScroll() {
      document.body.classList.remove('is-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }

    function open(name) {
      const drawer = qs(`[data-drawer="${name}"]`);
      if (!drawer) return;

      lastFocused = document.activeElement;
      activeName = name;

      drawer.classList.add('is-open');
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(() => overlay.classList.add('is-active'));
      }
      lockScroll();

      qsa(`[data-drawer-open="${name}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));

      setTimeout(() => {
        const target = qs(FOCUSABLE, drawer);
        if (target) target.focus();
        else drawer.focus();
      }, 220);
    }

    function close() {
      const drawer = current();
      const name = activeName;
      if (!name) return;

      if (drawer) drawer.classList.remove('is-open');
      if (overlay) {
        overlay.classList.remove('is-active');
        setTimeout(() => {
          if (!activeName) overlay.hidden = true;
        }, 320);
      }
      unlockScroll();

      qsa(`[data-drawer-open="${name}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

      activeName = null;
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function isOpen(name) {
      return activeName === name;
    }

    function trapFocus(e) {
      const drawer = current();
      if (e.key !== 'Tab' || !drawer) return;
      const focusables = qsa(FOCUSABLE, drawer).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function init() {
      overlay = qs('[data-overlay]');

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-drawer-open]');
        if (opener) {
          e.preventDefault();
          open(opener.getAttribute('data-drawer-open'));
          return;
        }

        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          close();
          return;
        }

        if (overlay && e.target === overlay) close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && activeName) {
          close();
          return;
        }
        trapFocus(e);
      });
    }

    return { init, open, close, isOpen };
  })();

  /* ---------------------------------------------------------------------
     Header search panel
     --------------------------------------------------------------------- */
  const Search = (function () {
    let abortController = null;

    function clearResults(input, resultsEl, loadingEl, hintEl) {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      if (resultsEl) resultsEl.innerHTML = '';
      if (loadingEl) loadingEl.hidden = true;
      if (input) input.setAttribute('aria-expanded', 'false');
      if (hintEl) hintEl.hidden = false;
    }

    function fetchResults(term, input, resultsEl, loadingEl) {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      if (loadingEl) loadingEl.hidden = false;

      const url =
        `${routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search` +
        '&resources[type]=product,collection,page,article&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: abortController.signal, headers: { Accept: 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          resultsEl.innerHTML = html;
          input.setAttribute('aria-expanded', 'true');
          if (loadingEl) loadingEl.hidden = true;
          Images.observe(resultsEl);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          if (loadingEl) loadingEl.hidden = true;
          resultsEl.innerHTML = '';
          Toast.show(strings.cartError || 'Er ging iets mis', true);
        });
    }

    function init() {
      const panel = qs('[data-search-panel]');
      if (!panel) return;

      const input = qs('[data-search-input]', panel);
      const resultsEl = qs('[data-predictive-search-results]', panel);
      const loadingEl = qs('[data-predictive-search-loading]', panel);
      const hintEl = qs('[data-predictive-search-hint]', panel);

      const open = () => {
        panel.hidden = false;
        requestAnimationFrame(() => {
          panel.classList.add('is-open');
          if (input) input.focus();
        });
        qsa('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      };

      const close = () => {
        panel.classList.remove('is-open');
        qsa('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        clearResults(input, resultsEl, loadingEl, hintEl);
        setTimeout(() => {
          panel.hidden = true;
        }, 400);
      };

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) {
          e.preventDefault();
          panel.classList.contains('is-open') ? close() : open();
        } else if (e.target.closest('[data-search-close]')) {
          e.preventDefault();
          close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
      });

      if (!input || !resultsEl) return;

      input.addEventListener(
        'input',
        debounce(() => {
          const term = input.value.trim();
          if (!term) {
            clearResults(input, resultsEl, loadingEl, hintEl);
            return;
          }
          if (hintEl) hintEl.hidden = true;
          fetchResults(term, input, resultsEl, loadingEl);
        }, 300)
      );

      // Roving focus between the input and the result links — Enter still
      // submits the form natively (full results page) when focus is on the
      // input itself.
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown') return;
        const first = qs('[data-predictive-search-option]', resultsEl);
        if (first) {
          e.preventDefault();
          first.focus();
        }
      });

      resultsEl.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const options = qsa('[data-predictive-search-option]', resultsEl);
        const current = options.indexOf(document.activeElement);
        if (current === -1) return;
        e.preventDefault();

        if (e.key === 'ArrowDown') {
          const next = options[current + 1];
          if (next) next.focus();
        } else if (current === 0) {
          input.focus();
        } else {
          options[current - 1].focus();
        }
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Desktop nav (keyboard support) + language dropdowns + mobile accordions
     --------------------------------------------------------------------- */
  const Nav = (function () {
    function init() {
      // Keyboard: Enter/Space on a parent link opens the mega panel.
      qsa('[data-nav-item]').forEach((item) => {
        const toggle = qs('[data-nav-toggle]', item);
        if (!toggle) return;

        const megaLinks = qsa('.mega__link', item);

        const closeMega = (returnFocus) => {
          item.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          if (returnFocus) toggle.focus();
        };

        on(toggle, 'keydown', (e) => {
          if (e.key === 'ArrowDown' || (e.key === 'Enter' && !item.classList.contains('is-open'))) {
            if (e.key === 'ArrowDown') e.preventDefault();
            item.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            const first = megaLinks[0];
            if (first && e.key === 'ArrowDown') first.focus();
          }
        });

        // Roving focus within the open mega panel + Escape to close.
        on(item, 'keydown', (e) => {
          if (e.key === 'Escape' && item.classList.contains('is-open')) {
            closeMega(true);
            return;
          }
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && megaLinks.length) {
            const idx = megaLinks.indexOf(document.activeElement);
            if (idx === -1) return;
            e.preventDefault();
            const next =
              e.key === 'ArrowDown'
                ? megaLinks[Math.min(idx + 1, megaLinks.length - 1)]
                : megaLinks[Math.max(idx - 1, 0)];
            next.focus();
          }
        });

        on(item, 'focusout', (e) => {
          if (!item.contains(e.relatedTarget)) {
            item.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
          }
        });

        on(item, 'mouseenter', () => toggle.setAttribute('aria-expanded', 'true'));
        on(item, 'mouseleave', () => {
          item.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        });
      });

      // Language dropdowns
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-lang-toggle]');
        const wrap = toggle ? toggle.closest('[data-lang-switch]') : null;

        qsa('[data-lang-switch]').forEach((sw) => {
          if (sw !== wrap) {
            sw.classList.remove('is-open');
            const t = qs('[data-lang-toggle]', sw);
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });

        if (toggle && wrap) {
          e.preventDefault();
          const nowOpen = !wrap.classList.contains('is-open');
          wrap.classList.toggle('is-open', nowOpen);
          toggle.setAttribute('aria-expanded', String(nowOpen));
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        qsa('[data-lang-switch].is-open').forEach((sw) => {
          sw.classList.remove('is-open');
          const t = qs('[data-lang-toggle]', sw);
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      });

      // Mobile nav accordions
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-mnav-toggle]');
        if (!toggle) return;
        e.preventDefault();
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Accordions (product page and elsewhere)
     --------------------------------------------------------------------- */
  const Accordions = (function () {
    function syncItem(trigger) {
      const item = trigger.closest('.accordion__item');
      if (item) item.classList.toggle('is-open', trigger.getAttribute('aria-expanded') === 'true');
    }

    function init() {
      qsa('[data-accordion-trigger]').forEach(syncItem);

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-accordion-trigger]');
        if (!trigger) return;
        e.preventDefault();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        syncItem(trigger);
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Product gallery
     --------------------------------------------------------------------- */
  const Gallery = (function () {
    let root, lightbox;

    function selectByMediaId(mediaId) {
      if (!root || !mediaId) return;

      const slide = qs(`[data-gallery-slide][data-media-id="${mediaId}"]`, root);
      if (!slide) return;

      qsa('[data-gallery-slide]', root).forEach((s) => s.classList.remove('is-active'));
      slide.classList.add('is-active');

      qsa('[data-gallery-thumb]', root).forEach((t) => {
        const active = t.getAttribute('data-media-id') === String(mediaId);
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && t.scrollIntoView) {
          t.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      Images.observe(slide);

      // Lightbox lives outside [data-gallery] (it's a full-viewport overlay,
      // not part of the sticky column), so it's synced separately here.
      if (lightbox) {
        const lbSlide = qs(`[data-lightbox-slide][data-media-id="${mediaId}"]`, lightbox);
        if (lbSlide) {
          qsa('[data-lightbox-slide]', lightbox).forEach((s) => s.classList.remove('is-active'));
          lbSlide.classList.add('is-active');
          Images.observe(lbSlide);
        }
      }
    }

    function step(delta) {
      const slides = qsa('[data-gallery-slide]', root);
      const current = slides.findIndex((s) => s.classList.contains('is-active'));
      const next = slides[current + delta];
      if (next) selectByMediaId(next.getAttribute('data-media-id'));
    }

    // Touch swipe — lets mobile shoppers flick between gallery/lightbox
    // images instead of only tapping thumbnails or arrow buttons.
    function addSwipe(el) {
      if (!el) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;

      el.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      el.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          step(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    function init() {
      root = qs('[data-gallery]');
      lightbox = qs('[data-drawer="lightbox"]');
      if (!root) return;

      addSwipe(qs('.gallery__main', root));
      if (lightbox) addSwipe(qs('.lightbox__stage', lightbox));

      root.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        e.preventDefault();
        selectByMediaId(thumb.getAttribute('data-media-id'));
      });

      root.addEventListener('keydown', (e) => {
        const thumb = e.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();

        const thumbs = qsa('[data-gallery-thumb]', root);
        const i = thumbs.indexOf(thumb);
        const next = thumbs[e.key === 'ArrowRight' ? i + 1 : i - 1];
        if (next) {
          next.focus();
          selectByMediaId(next.getAttribute('data-media-id'));
        }
      });

      if (lightbox) {
        lightbox.addEventListener('click', (e) => {
          if (e.target.closest('[data-lightbox-next]')) step(1);
          else if (e.target.closest('[data-lightbox-prev]')) step(-1);
        });

        lightbox.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') step(1);
          else if (e.key === 'ArrowLeft') step(-1);
        });
      }
    }

    return { init, selectByMediaId };
  })();

  /* ---------------------------------------------------------------------
     Garden mosaic — expanding tile row (hover/focus grows a tile)
     --------------------------------------------------------------------- */
  const Mosaic = (function () {
    function activate(group, tile) {
      qsa('[data-mosaic-tile]', group).forEach((t) => t.classList.remove('is-active'));
      tile.classList.add('is-active');
    }

    function init() {
      qsa('[data-mosaic]').forEach((group) => {
        qsa('[data-mosaic-tile]', group).forEach((tile) => {
          on(tile, 'mouseenter', () => activate(group, tile));
          on(tile, 'focus', () => activate(group, tile));
        });
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Quantity steppers (product form + cart lines)
     --------------------------------------------------------------------- */
  const Quantity = (function () {
    function step(wrap, delta) {
      const input = qs('[data-qty-input], [data-line-qty-input]', wrap);
      if (!input) return null;

      const min = parseInt(input.getAttribute('min') || '0', 10);
      const current = parseInt(input.value || '1', 10) || 0;
      const next = Math.max(min, current + delta);

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return next;
    }

    function init() {
      document.addEventListener('click', (e) => {
        const down = e.target.closest('[data-qty-down], [data-line-qty-down]');
        const up = e.target.closest('[data-qty-up], [data-line-qty-up]');
        if (!down && !up) return;

        e.preventDefault();
        const wrap = (down || up).closest('[data-qty]');
        if (wrap) step(wrap, down ? -1 : 1);
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Variant picker
     --------------------------------------------------------------------- */
  const Variants = (function () {
    let variants = [], form, idInput, priceWrap, addButton, addLabel, stickyPrice, stickyAdd, inventoryEl, pickupEl;

    function readSelected() {
      const values = [];
      qsa('[data-variant-option]:checked', form).forEach((input) => {
        const pos = parseInt(input.getAttribute('data-option-position'), 10);
        values[pos - 1] = input.value;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find((v) => v.options.every((opt, i) => opt === values[i])) || null;
    }

    // Grey out / disable option values that can't combine with the other
    // currently-selected options into any real variant — pure client-side
    // math against the JSON already on the page, no server round-trip.
    function updateOptionAvailability() {
      const selected = readSelected();

      qsa('[data-option-position]', form).forEach((fieldset) => {
        if (fieldset.tagName !== 'FIELDSET') return;
        const pos = parseInt(fieldset.getAttribute('data-option-position'), 10);

        qsa('[data-variant-option]', fieldset).forEach((input) => {
          const trial = selected.slice();
          trial[pos - 1] = input.value;

          const possible = variants.some((v) => v.options.every((opt, i) => trial[i] == null || opt === trial[i]));

          input.disabled = !possible;
          const label = input.nextElementSibling;
          if (label) {
            label.classList.toggle('variant-pill--unavailable', label.classList.contains('variant-pill') && !possible);
            label.classList.toggle('variant-swatch--unavailable', label.classList.contains('variant-swatch') && !possible);
          }
        });
      });
    }

    function renderPrice(variant) {
      if (!priceWrap) return;

      // No matching variant: hide the price rather than leave the previous
      // selection's price showing next to a disabled "unavailable" button.
      if (!variant) {
        priceWrap.hidden = true;
        return;
      }
      priceWrap.hidden = false;

      const onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      const priceEl = qs('.price', priceWrap);
      if (!priceEl) return;

      priceEl.classList.toggle('price--on-sale', !!onSale);

      const current = qs('.price__current', priceEl);
      if (current) current.textContent = variant.priceFormatted;

      let was = qs('.price__was', priceEl);
      if (onSale) {
        if (!was) {
          was = document.createElement('s');
          was.className = 'price__was';
          priceEl.appendChild(was);
        }
        was.textContent = variant.compareAtFormatted;
      } else if (was) {
        was.remove();
      }

      if (stickyPrice) stickyPrice.textContent = variant.priceFormatted;
    }

    function renderInventory(variant) {
      if (!inventoryEl) return;
      const threshold = parseInt(inventoryEl.getAttribute('data-threshold') || '0', 10);

      const showLowStock =
        variant &&
        threshold > 0 &&
        variant.inventoryManagement === 'shopify' &&
        variant.inventoryQuantity > 0 &&
        variant.inventoryQuantity <= threshold;

      inventoryEl.hidden = !showLowStock;
      if (showLowStock) {
        const tpl = variant.inventoryQuantity === 1 ? strings.lowStockOne : strings.lowStockOther;
        if (tpl) inventoryEl.textContent = tpl.replace('{{ count }}', variant.inventoryQuantity).replace('{{count}}', variant.inventoryQuantity);
      }
    }

    function setAvailability(variant) {
      const available = variant && variant.available;

      [addButton, stickyAdd].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !available;
      });

      if (addLabel) {
        addLabel.textContent = !variant
          ? strings.unavailable || 'Niet beschikbaar'
          : available
            ? strings.addToCart || 'In winkelmandje'
            : strings.soldOut || 'Uitverkocht';
      }
    }

    function updateLegends() {
      qsa('[data-option-position]', form).forEach((fieldset) => {
        if (fieldset.tagName !== 'FIELDSET') return;
        const checked = qs('[data-variant-option]:checked', fieldset);
        const label = qs('[data-option-selected]', fieldset);
        if (checked && label) label.textContent = checked.value;
      });
    }

    function onChange() {
      const values = readSelected();
      const variant = findVariant(values);

      updateLegends();
      updateOptionAvailability();
      setAvailability(variant);
      renderPrice(variant);
      renderInventory(variant);

      // Pickup availability is only known for the initially-rendered variant
      // (see main-product.liquid) — hide rather than show stale per-location
      // stock for a variant we have no data for.
      if (pickupEl) pickupEl.hidden = true;

      if (!variant) return;

      if (idInput) idInput.value = variant.id;

      if (variant.mediaId) Gallery.selectByMediaId(variant.mediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function init() {
      const dataEl = qs('[data-variant-json]');
      form = qs('[data-product-form]');
      if (!dataEl || !form) return;

      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (err) {
        return;
      }

      idInput = qs('[data-variant-id]', form);
      priceWrap = qs('[data-price-wrapper]');
      addButton = qs('[data-add-button]', form);
      addLabel = qs('[data-add-label]', form);
      stickyPrice = qs('[data-sticky-price]');
      stickyAdd = qs('[data-sticky-add]');
      inventoryEl = qs('[data-inventory-message]');
      pickupEl = qs('[data-pickup-availability]');

      updateOptionAvailability();

      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-variant-option]')) onChange();
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Cart — AJAX add / change / remove, drawer refresh via Section Rendering
     --------------------------------------------------------------------- */
  const Cart = (function () {
    const DRAWER_SECTION = 'cart-drawer';

    function cartSectionId() {
      const el = qs('[data-cart-section]');
      return el ? el.getAttribute('data-section-id') : null;
    }

    function sectionsToRequest() {
      const list = [DRAWER_SECTION];
      const id = cartSectionId();
      if (id) list.push(id);
      return list;
    }

    function swapSection(id, html) {
      const target = document.getElementById('shopify-section-' + id);
      if (!target || !html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.getElementById('shopify-section-' + id);
      if (!fresh) return;

      const wasOpen = !!qs('[data-cart-drawer].is-open', target);
      target.innerHTML = fresh.innerHTML;

      if (wasOpen) {
        const drawer = qs('[data-cart-drawer]', target);
        if (drawer) drawer.classList.add('is-open');
      }

      ScrollReveal.observe(target);
      Images.observe(target);
    }

    function applySections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => swapSection(id, sections[id]));
    }

    function updateCount(count) {
      const badge = qs('[data-cart-count]');
      const srText = qs('[data-cart-count-text]');

      if (badge) {
        badge.textContent = count;
        badge.classList.toggle('is-visible', count > 0);
        badge.classList.add('is-bumped');
        setTimeout(() => badge.classList.remove('is-bumped'), 420);
      }

      if (srText) {
        const tpl = count === 1 ? strings.itemsOne : strings.itemsOther;
        if (tpl) srText.textContent = tpl.replace('{{ count }}', count).replace('{{count}}', count);
      }
    }

    function refreshCount() {
      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          updateCount(cart.item_count);
          return cart;
        })
        .catch(() => null);
    }

    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(
          Object.assign({ sections: sectionsToRequest(), sections_url: window.location.pathname }, body)
        )
      }).then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw data;
        return data;
      });
    }

    function add(variantId, quantity, button) {
      if (button) button.classList.add('is-loading');

      return post(routes.cartAdd + '.js', { items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] })
        .then((data) => {
          applySections(data.sections);
          return refreshCount();
        })
        .then(() => {
          Toast.show(strings.added || 'Toegevoegd aan winkelmandje');
          if ((T.settings && T.settings.cartType) !== 'page') Drawers.open(DRAWER_SECTION);
        })
        .catch((err) => {
          const message = (err && (err.description || err.message)) || strings.cartError || 'Er ging iets mis';
          Toast.show(message, true);
          const errEl = qs('[data-form-error]');
          if (errEl) {
            errEl.textContent = message;
            errEl.hidden = false;
          }
        })
        .finally(() => {
          if (button) button.classList.remove('is-loading');
        });
    }

    function change(line, quantity, lineEl) {
      if (lineEl) lineEl.classList.add('is-updating');
      const removing = Number(quantity) === 0;

      return post(routes.cartChange + '.js', { line: Number(line), quantity: Number(quantity) })
        .then((cart) => {
          applySections(cart.sections);
          updateCount(cart.item_count);

          // If the section swap did not happen we would be left showing stale
          // lines — fall back to a reload so the empty state is correct.
          const swapped = cart.sections && Object.keys(cart.sections).length > 0;
          if (!swapped && qs('[data-cart-section]')) window.location.reload();

          // Quantity changes are visible in the swapped markup; removal is a
          // more significant, easy-to-miss action, so it gets an explicit
          // screen-reader announcement via the shared toast (aria-live).
          if (removing) Toast.show(strings.removed || 'Verwijderd uit winkelmandje');
        })
        .catch(() => {
          Toast.show(strings.cartError || 'Er ging iets mis', true);
        })
        .finally(() => {
          if (lineEl) lineEl.classList.remove('is-updating');
        });
    }

    function init() {
      // Open drawer from the header cart icon
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (!toggle) return;
        if ((T.settings && T.settings.cartType) === 'page') return;
        if (!qs('[data-cart-drawer]')) return;

        e.preventDefault();
        Drawers.open(DRAWER_SECTION);
      });

      // Discount code: Shopify's cart form has no native "discount" field —
      // appending ?discount=CODE to the form action before submit carries it
      // through the cart -> checkout redirect the "checkout" button triggers.
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-cart-form]');
        if (!form) return;

        const discountInput = form.querySelector('[name="discount"]');
        const code = discountInput && discountInput.value.trim();
        if (!code) return;

        const url = new URL(form.action, window.location.origin);
        url.searchParams.set('discount', code);
        form.action = url.toString();
      });

      // Product form submit + quick-add forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form], [data-quick-add-form]');
        if (!form) return;

        e.preventDefault();

        const idField = form.querySelector('[name="id"]');
        const qtyField = form.querySelector('[name="quantity"]');
        const button = form.querySelector('[data-add-button], button[type="submit"]');
        const errEl = qs('[data-form-error]');

        if (errEl) errEl.hidden = true;
        if (!idField || !idField.value) return;

        add(idField.value, qtyField ? qtyField.value : 1, button);
      });

      // Sticky mobile buy bar
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sticky-add]');
        if (!btn) return;
        e.preventDefault();

        const form = qs('[data-product-form]');
        if (!form) return;

        const idField = form.querySelector('[name="id"]');
        const qtyField = form.querySelector('[name="quantity"]');
        add(idField.value, qtyField ? qtyField.value : 1, btn);
      });

      // Buy now — add then straight to checkout
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-buy-now]');
        if (!btn) return;
        e.preventDefault();

        const form = qs('[data-product-form]');
        if (!form) return;

        const idField = form.querySelector('[name="id"]');
        const qtyField = form.querySelector('[name="quantity"]');

        btn.classList.add('is-loading');
        post(routes.cartAdd + '.js', {
          items: [{ id: Number(idField.value), quantity: Number(qtyField ? qtyField.value : 1) }]
        })
          .then(() => {
            window.location.href = '/checkout';
          })
          .catch(() => {
            btn.classList.remove('is-loading');
            Toast.show(strings.cartError || 'Er ging iets mis', true);
          });
      });

      // Line quantity changes (cart page + drawer)
      document.addEventListener(
        'change',
        debounce((e) => {
          const input = e.target.closest('[data-line-qty-input]');
          if (!input) return;

          const lineEl = input.closest('[data-line-item]');
          if (!lineEl) return;

          change(lineEl.getAttribute('data-line'), input.value, lineEl);
        }, 250)
      );

      // Remove line
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-line-remove]');
        if (!btn) return;
        e.preventDefault();

        const lineEl = btn.closest('[data-line-item]');
        if (!lineEl) return;

        change(lineEl.getAttribute('data-line'), 0, lineEl);
      });
    }

    return { init, add, refreshCount };
  })();

  /* ---------------------------------------------------------------------
     Sticky mobile buy bar visibility
     --------------------------------------------------------------------- */
  const StickyBuy = (function () {
    function init() {
      const bar = qs('[data-sticky-buy]');
      const buybox = qs('[data-buybox]');
      if (!bar || !buybox || !('IntersectionObserver' in window)) return;

      const actions = qs('.buybox__actions', buybox) || buybox;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show once the add-to-cart control has scrolled out of view upwards.
            const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', past);
          });
        },
        { threshold: 0 }
      );

      observer.observe(actions);
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Collection toolbar — sort + facets + load more
     --------------------------------------------------------------------- */
  const Collection = (function () {
    function init() {
      const sortSelect = qs('[data-sort-select]');
      if (sortSelect) {
        on(sortSelect, 'change', () => {
          const form = sortSelect.closest('form');
          if (form) form.submit();
        });
      }

      // Checkboxes auto-submit; price range and no-JS fallback use the
      // visible "Toepassen" button. Native GET serialization means every
      // field (hidden sort_by, checked facets, price inputs) is included
      // correctly with zero manual param-building.
      const facetForm = qs('[data-facet-form]');
      if (facetForm) {
        facetForm.addEventListener('change', (e) => {
          if (!e.target.matches('[data-facet-input]')) return;
          facetForm.submit();
        });
      }

      // Load more
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-load-more]');
        if (!btn) return;
        e.preventDefault();

        const url = btn.getAttribute('data-next-url');
        const grid = qs('[data-product-grid]');
        if (!url || !grid) return;

        btn.classList.add('is-loading');

        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newGrid = doc.querySelector('[data-product-grid]');
            const newBtn = doc.querySelector('[data-load-more]');

            if (newGrid) {
              Array.from(newGrid.children).forEach((child) => grid.appendChild(child));
              ScrollReveal.observe(grid);
              Images.observe(grid);
            }

            if (newBtn && newBtn.getAttribute('data-next-url')) {
              btn.setAttribute('data-next-url', newBtn.getAttribute('data-next-url'));
              btn.classList.remove('is-loading');
            } else {
              const wrap = btn.closest('.load-more-wrap');
              if (wrap) wrap.remove();
            }

            window.history.replaceState({}, '', url);
          })
          .catch(() => {
            btn.classList.remove('is-loading');
            Toast.show(strings.cartError || 'Er ging iets mis', true);
          });
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  const Recommendations = (function () {
    function init() {
      const section = qs('[data-related]');
      if (!section) return;

      const url = section.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-related]');
          if (!fresh) return;

          // Keep the server-rendered fallback when the API returns nothing.
          if (!fresh.querySelector('.pcard')) return;

          section.innerHTML = fresh.innerHTML;
          ScrollReveal.observe(section);
          Images.observe(section);
        })
        .catch(() => {
          /* fallback grid stays in place */
        });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Announcement rotator
     --------------------------------------------------------------------- */
  const Announcement = (function () {
    function init() {
      const track = qs('[data-announcement-track]');
      if (!track) return;

      const slides = qsa('[data-announcement-slide]', track);
      if (slides.length < 2 || prefersReducedMotion()) return;

      const speed = parseInt(track.getAttribute('data-rotate'), 10) || 5000;

      let i = 0;
      setInterval(() => {
        slides[i].hidden = true;
        i = (i + 1) % slides.length;
        slides[i].hidden = false;
      }, speed);
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Parallax (story section)
     --------------------------------------------------------------------- */
  const Parallax = (function () {
    function init() {
      const els = qsa('[data-parallax]');
      if (!els.length || prefersReducedMotion()) return;

      let ticking = false;

      const update = () => {
        els.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;

          const strength = parseFloat(el.getAttribute('data-parallax')) || 15;
          const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
          const shift = -progress * strength;

          const img = el.querySelector('img');
          if (img) img.style.transform = `translate3d(0, ${shift.toFixed(2)}%, 0)`;
        });
        ticking = false;
      };

      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );

      update();
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Customer account helpers
     --------------------------------------------------------------------- */
  const Account = (function () {
    function init() {
      // Login <-> recover password
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-recover-toggle]');
        if (!toggle) return;
        e.preventDefault();

        const showRecover = toggle.getAttribute('data-recover-toggle') === 'recover';
        const recover = qs('[data-recover-panel]');
        const login = qs('[data-login-panel]');
        if (recover) recover.hidden = !showRecover;
        if (login) login.hidden = showRecover;
      });

      if (window.location.hash === '#recover') {
        const recover = qs('[data-recover-panel]');
        const login = qs('[data-login-panel]');
        if (recover) recover.hidden = false;
        if (login) login.hidden = true;
      }

      // Address edit panels
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-address-toggle]');
        if (!toggle) return;
        e.preventDefault();

        const panel = document.getElementById(toggle.getAttribute('data-address-toggle'));
        if (panel) panel.hidden = !panel.hidden;
      });

      // Destructive actions ask first
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-confirm]');
        if (!btn) return;
        if (!window.confirm(btn.getAttribute('data-confirm'))) e.preventDefault();
      });

      // Country/province linkage
      if (window.Shopify && window.Shopify.CountryProvinceSelector) {
        qsa('[data-address-country]').forEach((countryEl) => {
          const wrap = countryEl.closest('form');
          const provinceEl = wrap ? qs('[data-address-province]', wrap) : null;
          const provinceWrap = wrap ? qs('[data-address-province-wrap]', wrap) : null;
          if (!provinceEl || !provinceWrap) return;

          new window.Shopify.CountryProvinceSelector(countryEl.id, provinceEl.id, {
            hideElement: provinceWrap.id
          });
        });
      }
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Hero background video — stop autoplay for reduced-motion preference
     --------------------------------------------------------------------- */
  const HeroVideo = (function () {
    function init() {
      if (!prefersReducedMotion()) return;
      qsa('.hero__video').forEach((video) => {
        video.pause();
        video.removeAttribute('autoplay');
      });
    }

    return { init };
  })();

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init() {
    Toast.init();
    ScrollReveal.init();
    Images.init();
    Header.init();
    Drawers.init();
    Search.init();
    Nav.init();
    Accordions.init();
    Gallery.init();
    Mosaic.init();
    Quantity.init();
    Variants.init();
    Cart.init();
    StickyBuy.init();
    Collection.init();
    Recommendations.init();
    Announcement.init();
    Parallax.init();
    HeroVideo.init();
    Account.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor hooks
  document.addEventListener('shopify:section:load', () => {
    ScrollReveal.observe();
    Images.observe();
    Gallery.init();
    Variants.init();
    StickyBuy.init();
    Parallax.init();
  });

  document.addEventListener('shopify:section:select', (e) => {
    const drawer = qs('[data-drawer]', e.target);
    if (drawer && drawer.hasAttribute('data-cart-drawer')) {
      Drawers.open('cart-drawer');
    }
  });

  window.BourtangeTheme = { Cart, Drawers, Toast };
})();
